import { lazy, Suspense, type ComponentType } from 'react';
import { useTranslation } from 'react-i18next';
import type { TabModel } from '@/features/shell/tabs/tabsStore';
import { ScreenLoadProvider, SuspenseHold } from '@/shared/ui/screenLoad';
import { EmptyState } from '@/shared/ui/EmptyState';
import { MetadataChangeBar } from '@/features/metadata-watch/ui/MetadataChangeBar';
import { MetadataHighlightProvider } from '@/features/metadata-watch/ui/HighlightContext';

/**
 * Ekran tipi → bileşen dispatch'i. Part B/C tamamlandıkça buraya bağlanır.
 * Bilinmeyen tip: placeholder (Blazor'daki gibi sessizce boş grid açılmaz, açıkça söylenir).
 */
const PlaceholderScreen = lazy(() => import('./PlaceholderScreen'));
const MasterGridScreen = lazy(() => import('./master/MasterGridScreen'));
const MasterDetailScreen = lazy(() => import('./master/MasterDetailScreen'));
const EditFormTabScreen = lazy(() => import('./editform/EditFormTabScreen'));
const SystemLogsScreen = lazy(() => import('./systemlogs/SystemLogsScreen'));
const MonitorScreen = lazy(() => import('@/features/monitor/MonitorScreen'));
const PivotScreen = lazy(() => import('@/features/pivot/PivotScreen'));
const FormBuilderScreen = lazy(() => import('@/features/formbuilder/FormBuilderScreen'));
const ReportTabScreen = lazy(() => import('@/features/reports/ReportTabScreen'));
const DashboardScreen = lazy(() => import('@/features/dashboard/DashboardScreen'));
const ReportDesignerTabScreen = lazy(() => import('@/features/reports/ReportDesignerTabScreen'));
const AccountScreen = lazy(() => import('@/features/account/AccountScreen'));
const PolicyScreen = lazy(() => import('@/features/policy/PolicyScreen'));
const UserScreen = lazy(() => import('@/features/users/UserScreen'));
const ScreenPermissionsScreen = lazy(() => import('@/features/screenPermissions/ScreenPermissionsScreen'));
const UserAuthorizationScreen = lazy(() => import('@/features/userAuthorization/UserAuthorizationScreen'));
const WorkerScreen = lazy(() => import('@/features/worker/WorkerScreen'));
const SpParamsTabScreen = lazy(() => import('@/features/actions/ui/SpParamsTabScreen'));
const ObjectTabScreen = lazy(() => import('./object/ObjectTabScreen'));
const AiReportTabScreen = lazy(() => import('@/features/ai/ui/AiReportTabScreen'));
const AiAdminTabScreen = lazy(() => import('@/features/ai/ui/AiAdminTabScreen'));
const SingleRecordFormScreen = lazy(() => import('./singlerecord/SingleRecordFormScreen'));

/**
 * Ekran tipi adı → bileşen. Eksik tipler PlaceholderScreen'e düşer.
 *
 * Dönüş `null` olabilir: açılış örtüsü ekrandayken ekran kendi göstergesini çizmez.
 */
type ScreenComponent = ComponentType<{ tab: TabModel; active: boolean }>;
const SCREEN_COMPONENTS: Record<string, ScreenComponent> = {
  master: MasterGridScreen,
  masterdetail: MasterDetailScreen,
  node: MasterDetailScreen, // detay yoksa master grid'e düşer
  editform: EditFormTabScreen, // New Page düzenleme modu (özel sekme)
  logviewer: SystemLogsScreen,
  monitor: MonitorScreen,
  pivotgrid: PivotScreen,
  pivot: PivotScreen,
  formbuilder: FormBuilderScreen,
  form: FormBuilderScreen, // ForceViewMode: FormBuilder salt-okunur açılır
  report: ReportTabScreen, // ShowDialog=false rapor işlemi (özel sekme)
  dashboard: DashboardScreen, // menü/ana sayfa listesinden açılan DevExpress dashboard'ı
  reportdesigner: ReportDesignerTabScreen, // ActionTypeId = Rapor Tasarımcısı olan veri işlemi
  account: AccountScreen, // profil menüsü → Hesap bilgilerim
  policy: PolicyScreen, // rol filtre kuralları (RoleFilterPolicyLayout karşılığı)
  user: UserScreen, // kullanıcı yönetimi (UserManagementLayout karşılığı)
  screen: ScreenPermissionsScreen, // ekran → kullanıcı yetkileri (ScreenLayout karşılığı)
  authorization: UserAuthorizationScreen, // toplu yetkilendirme (UserAuthorizationLayout karşılığı)
  worker: WorkerScreen, // zamanlanmış görevler (WorkerTaskLayout karşılığı)
  spparams: SpParamsTabScreen, // sekmeye taşınmış SP parametre formu (#445)
  objectgrid: ObjectTabScreen, // ekran kaydı olmayan nesne, ObjectId ile (#533; bkz. lib/objectTab)
  aireport: AiReportTabScreen, // menüye kaydedilmiş AI raporu (AI Raporları bölümü)
  aiadmin: AiAdminTabScreen, // Octo AI Ayarları: bağlantılar, bilgi dokümanları, skill'ler (sistem rolü)
  singlerecordform: SingleRecordFormScreen, // tek kayıt formu: açılışta son kayıt New Page formunda (#569)
};

export function ScreenHost({ tab, active }: { tab: TabModel; active: boolean }) {
  const { t } = useTranslation();
  const type = (tab.screenTypeName ?? tab.kind).toLowerCase();
  return (
    <MetadataHighlightProvider screenId={tab.screenId}>
      {/* Tasarım değişikliği şeridi ekran tipinden bağımsız: her sekmenin üstünde aynı yerde. */}
      <MetadataChangeBar screenId={tab.screenId} />
      {/* Ekranın bütün beklemeleri (lazy parça, demet, grid verisi) tek göstergede
          birleşir; örtü ancak hepsi bitince kalkar. */}
      <ScreenLoadProvider kind="screen">
        <Suspense fallback={<SuspenseHold />}>
          {type ? (
            (() => {
              const C = SCREEN_COMPONENTS[type] ?? PlaceholderScreen;
              return <C tab={tab} active={active} />;
            })()
          ) : (
            <EmptyState title={t('screen.notFound')} />
          )}
        </Suspense>
      </ScreenLoadProvider>
    </MetadataHighlightProvider>
  );
}
